import React, { useEffect, useRef, useState } from 'react';

const FUNCTIONS_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  'https://us-central1-fotocarnetmadrid.cloudfunctions.net';

function getAmountCents(amount) {
  return String(Math.round(Number.parseFloat(String(amount)) * 100) || 500);
}

function getLanguage() {
  const language =
    document?.documentElement?.lang || navigator?.language || 'es';
  return String(language).slice(0, 2).toLowerCase() || 'es';
}

function supportsApplePay() {
  if (typeof window === 'undefined' || !window.isSecureContext) {
    return false;
  }

  if (!window.ApplePaySession) {
    return false;
  }

  try {
    return Boolean(window.ApplePaySession.canMakePayments());
  } catch {
    return false;
  }
}

async function createApplePaySession({ amount, currency }) {
  const response = await fetch(`${FUNCTIONS_BASE_URL}/initPaycometPayment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      amount: getAmountCents(amount),
      currency,
      language: getLanguage(),
      returnOrigin: window.location.origin,
      returnMethod: 'APPLE_PAY',
      productDescription: 'fotopacasa.es',
    }),
  });

  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok || !data || !data.success || !data.challengeUrl) {
    throw new Error(
      (data && data.error) ||
        'No se ha podido iniciar el pago con Apple Pay.'
    );
  }

  return data.challengeUrl;
}

const ApplePayOption = ({
  amount = '5.00',
  currency = 'EUR',
  description = 'Paga rapido y seguro con Apple Pay.',
  onSuccess,
  onError,
}) => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const popupRef = useRef(null);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onError, onSuccess]);

  useEffect(() => {
    setIsAvailable(supportsApplePay());
  }, []);

  useEffect(() => {
    const handleHostedResult = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const payload = event.data || {};
      if (
        payload.type !== 'paycomet-hosted-result' ||
        payload.method !== 'APPLE_PAY'
      ) {
        return;
      }

      setIsProcessing(false);

      if (payload.status === 'ok') {
        setError(null);
        if (onSuccessRef.current) {
          onSuccessRef.current({
            paymentMethod: 'APPLE_PAY',
            amount,
            currency,
            status: 'completed',
            gatewayResponse: payload,
          });
        }
        return;
      }

      const applePayError = new Error(
        payload.message || 'El pago con Apple Pay no se ha completado.'
      );
      setError(applePayError.message);
      if (onErrorRef.current) {
        onErrorRef.current(applePayError);
      }
    };

    window.addEventListener('message', handleHostedResult);
    return () => window.removeEventListener('message', handleHostedResult);
  }, [amount, currency]);

  useEffect(() => {
    if (!isProcessing) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        popupRef.current = null;
        setIsProcessing(false);
      }
    }, 500);

    return () => window.clearInterval(intervalId);
  }, [isProcessing]);

  const handleClick = async () => {
    setError(null);

    const popup = window.open(
      '',
      'paycometApplePay',
      'popup=yes,width=480,height=720'
    );

    if (!popup) {
      const popupError = new Error(
        'No se ha podido abrir Apple Pay. Permite ventanas emergentes e intentalo de nuevo.'
      );
      setError(popupError.message);
      if (onErrorRef.current) {
        onErrorRef.current(popupError);
      }
      return;
    }

    popupRef.current = popup;
    setIsProcessing(true);

    try {
      const challengeUrl = await createApplePaySession({ amount, currency });
      popup.location = challengeUrl;
      popup.focus();
    } catch (err) {
      try {
        popup.close();
      } catch {
        // ignore popup closing issues
      }

      popupRef.current = null;
      setIsProcessing(false);
      const message =
        err.message || 'No se ha podido iniciar el pago con Apple Pay.';
      setError(message);
      if (onErrorRef.current) {
        onErrorRef.current(new Error(message));
      }
    }
  };

  if (!isAvailable) {
    return null;
  }

  return (
    <div className="payment-apple-pay">
      <p className="payment-copy">{description}</p>
      <button
        type="button"
        className="apple-pay-button"
        onClick={handleClick}
        disabled={isProcessing}
      >
        {isProcessing ? 'Abriendo Apple Pay...' : 'Pagar con Apple Pay'}
      </button>
      {error && <p className="payment-inline-error">{error}</p>}
    </div>
  );
};

export default ApplePayOption;
